import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  MessageSquare,
  Calendar,
  Code,
  Search,
  Wrench,
  Bot,
  Settings,
} from 'lucide-react'
import type { Tool } from '@/services/tools'

// Category icon mapping
const CATEGORY_ICONS: Record<string, typeof MessageSquare> = {
  communication: MessageSquare,
  productivity: Calendar,
  development: Code,
  search: Search,
  custom: Wrench,
}

// Category gradient colors
const CATEGORY_GRADIENTS: Record<string, string> = {
  communication: 'from-blue-500/20 to-cyan-500/20',
  productivity: 'from-amber-500/20 to-orange-500/20',
  development: 'from-purple-500/20 to-pink-500/20',
  search: 'from-green-500/20 to-emerald-500/20',
  custom: 'from-gray-500/20 to-slate-500/20',
}

// Category icon colors
const CATEGORY_ICON_COLORS: Record<string, string> = {
  communication: 'text-blue-400',
  productivity: 'text-amber-400',
  development: 'text-purple-400',
  search: 'text-green-400',
  custom: 'text-gray-400',
}

interface ToolCardProps {
  tool: Tool
  onClick?: () => void
  onConfigure?: () => void
}

export function ToolCard({ tool, onClick, onConfigure }: ToolCardProps) {
  const { t } = useTranslation('tools')

  const category = tool.category || 'custom'
  const Icon = CATEGORY_ICONS[category] || Wrench
  const gradient = CATEGORY_GRADIENTS[category] || CATEGORY_GRADIENTS.custom
  const iconColor = CATEGORY_ICON_COLORS[category] || CATEGORY_ICON_COLORS.custom

  // Check if tool type supports configuration
  const isConfigurable = tool.tool_type === 'mcp' || tool.tool_type === 'api'

  const handleConfigureClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onConfigure?.()
  }

  return (
    <Card
      variant="glass"
      className="p-6 cursor-pointer group hover:border-cyan-500/30 transition-all duration-200"
      onClick={onClick}
    >
      {/* Header: Icon + Status */}
      <div className="flex items-start justify-between mb-4">
        {/* Icon */}
        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
          <Icon className={`w-7 h-7 ${iconColor}`} />
        </div>

        {/* Status Badge */}
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            tool.is_configured
              ? 'bg-emerald-500/20 text-emerald-400'
              : 'bg-gray-500/20 text-gray-400'
          }`}
        >
          {tool.is_configured ? t('card.configured') : t('card.available')}
        </span>
      </div>

      {/* Name & Description */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white mb-1 truncate">
          {tool.name}
        </h3>
        <p className="text-sm text-gray-400 line-clamp-2 min-h-[2.5rem]">
          {tool.description || '-'}
        </p>
      </div>

      {/* Footer: Category + Configure/Agent count */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span className={`capitalize ${iconColor}`}>
          {t(`categories.${category}`)}
        </span>
        <div className="flex items-center gap-3">
          {isConfigurable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleConfigureClick}
              className="h-7 px-2 text-xs text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
            >
              <Settings className="w-3.5 h-3.5 mr-1" />
              {t('card.configure')}
            </Button>
          )}
          <div className="flex items-center gap-1.5">
            <Bot className="w-3.5 h-3.5" />
            <span>
              {tool.agent_count === 0
                ? t('card.noAgents')
                : t('card.agents', { count: tool.agent_count })}
            </span>
          </div>
        </div>
      </div>
    </Card>
  )
}
